import { useState, useEffect, useRef } from 'react';
import type { Project, MarketAnalysis, Persona } from '@/utils/pocketbase';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractPersonaName } from '@/utils/markdown';

import { useTranslation } from 'react-i18next';
interface PositioningCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  projects: Project[];
  marketAnalyses: MarketAnalysis[];
  personas: Persona[];
  initialData?: FormData;
  isEditing?: boolean;
  isLoading?: boolean;
}

export interface FormData {
  project_id: string;
  persona_id: string;
  market_analysis_id: string;
  competitors: string;
  unique_advantage: string;
  price_segment: string;
  sales_channels: string;
  mission: string;
  comment: string;
}

export function PositioningCreationModal({
  isOpen,
  onClose,
  onSubmit,
  projects,
  marketAnalyses,
  personas,
  initialData,
  isEditing = false,
  isLoading = false
}: PositioningCreationModalProps) {
  const { t } = useTranslation('positioning');
  const [formData, setFormData] = useState<FormData>({
    project_id: '',
    persona_id: '',
    market_analysis_id: '',
    competitors: '',
    unique_advantage: '',
    price_segment: '',
    sales_channels: '',
    mission: '',
    comment: ''
  });

  // Refs for textarea fields
  const competitorsRef = useRef<HTMLTextAreaElement>(null);
  const uniqueAdvantageRef = useRef<HTMLTextAreaElement>(null);
  const salesChannelsRef = useRef<HTMLTextAreaElement>(null);
  const missionRef = useRef<HTMLTextAreaElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Helper function to adjust textarea height
  const adjustHeight = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Adjust heights when formData changes
  useEffect(() => {
    adjustHeight(competitorsRef.current);
    adjustHeight(uniqueAdvantageRef.current);
    adjustHeight(salesChannelsRef.current);
    adjustHeight(missionRef.current);
    adjustHeight(commentRef.current);
  }, [formData]);

  // Filter personas and analyses by selected project
  const filteredPersonas = formData.project_id
    ? personas.filter(p => p.project === formData.project_id)
    : personas;

  const filteredAnalyses = formData.project_id
    ? marketAnalyses.filter(a => a.project === formData.project_id)
    : marketAnalyses;

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const isValid = formData.project_id && formData.persona_id && formData.market_analysis_id;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-[16px]"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-[24px] p-[24px] md:p-[40px] max-w-[580px] w-full max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <GenerationProgress
            title={t('modal.progressTitle')}
            message={isEditing
              ? t('modal.progressMessageEdit')
              : t('modal.progressMessageCreate')}
          />
        ) : (
          // Form State
          <>
            {/* Header */}
            <div className="flex flex-col gap-[16px] mb-[32px]">
              <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d]">
                {isEditing ? t('modal.titleEdit') : t('modal.titleCreate')}
              </h2>
              <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">
                {t('modal.subtitle')}
              </p>
            </div>

            {/* Form Fields */}
            <div className="flex flex-col gap-[24px] mb-[32px]">
              {/* Project Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelProduct')}
                </label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    project_id: value,
                    persona_id: '',
                    market_analysis_id: ''
                  })}
                  disabled={isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Persona Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelPersona')}
                </label>
                <Select
                  value={formData.persona_id}
                  onValueChange={(value) => setFormData({ ...formData, persona_id: value })}
                  disabled={isEditing || !formData.project_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPersonas.map(persona => (
                      <SelectItem key={persona.id} value={persona.id}>
                        {extractPersonaName(persona)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Market Analysis Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelMarketAnalysis')}
                </label>
                <Select
                  value={formData.market_analysis_id}
                  onValueChange={(value) => setFormData({ ...formData, market_analysis_id: value })}
                  disabled={isEditing || !formData.project_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredAnalyses.map((analysis, index) => {
                      const project = projects.find(p => p.id === analysis.project);
                      return (
                        <SelectItem key={analysis.id} value={analysis.id}>
                          {t('modal.analysisItemTitle', { projectName: project?.name || t('defaultProjectName')})}{filteredAnalyses.length > 1 ? ` (${index + 1})` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Competitors */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelCompetitors')}
                </label>
                <textarea
                  ref={competitorsRef}
                  value={formData.competitors}
                  onChange={(e) => setFormData({ ...formData, competitors: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={3}
                  placeholder={t('modal.placeholderCompetitors')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Unique Advantage */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelUniqueAdvantage')}
                </label>
                <textarea
                  ref={uniqueAdvantageRef}
                  value={formData.unique_advantage}
                  onChange={(e) => setFormData({ ...formData, unique_advantage: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={3}
                  placeholder={t('modal.placeholderUniqueAdvantage')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Price Segment */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelPriceSegment')}
                </label>
                <Select
                  value={formData.price_segment}
                  onValueChange={(value) => setFormData({ ...formData, price_segment: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderPriceSegment')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="premium">{t('modal.priceSegmentPremium')}</SelectItem>
                    <SelectItem value="medium">{t('modal.priceSegmentMedium')}</SelectItem>
                    <SelectItem value="budget">{t('modal.priceSegmentBudget')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sales Channels */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelSalesChannels')}
                </label>
                <textarea
                  ref={salesChannelsRef}
                  value={formData.sales_channels}
                  onChange={(e) => setFormData({ ...formData, sales_channels: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderSalesChannels')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Mission */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelMission')}
                </label>
                <textarea
                  ref={missionRef}
                  value={formData.mission}
                  onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderMission')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Comment */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelComment')}
                </label>
                <textarea
                  ref={commentRef}
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderComment')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-[24px]">
              <button
                onClick={onClose}
                className="
                  flex-1
                  bg-transparent
                  border border-[var(--grey-accent,#eeeeee)] border-solid
                  text-[color:var(--black-text,#0d0d0d)]
                  px-[24px] py-[12px]
                  rounded-[16px]
                  font-segoe font-semibold text-[16px] leading-[24px]
                  text-center
                  hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1]
                  transition-colors
                "
              >
                {t('modal.buttonClose')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid}
                className={`
                  flex-1
                  rounded-[16px] px-[24px] py-[12px]
                  font-segoe font-semibold text-[16px] leading-[24px]
                  text-center
                  transition-colors
                  ${isValid
                    ? 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
                    : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
                  }
                `}
              >
                {isEditing ? t('modal.buttonUpdate') : t('modal.buttonCreate')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
